import React, { useCallback, useEffect, useState } from 'react';

import type { Organization } from '@rtkwlf/eloc';
import type { ComboboxOption, SelectOption } from '@rtkwlf/fenrir-react';
import {
  Button,
  Combobox,
  Heading,
  HStack,
  Primitive,
  size,
  Text,
  VStack,
} from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';

import { useOrganizations } from '@apis/eloc/queries/use-organizations';
import { FullScreenLoader } from '@packages/components/loader/full-screen-loader';
import { useDocumentTitle } from '@packages/fenrir/hooks/use-document-title';
import { useOrganizationContext } from '@packages/legacy/organization-context';
import { isEmpty } from '@packages/utils/base.utils';
import { SadWolfError } from 'ae-components/sadWolfError/SadWolfError';
import { STORAGE_KEYS } from 'ae-constants/constants';

import { DashboardAppBar } from '../../layout/dashboard-app-bar/dashboard-app-bar';

const OrganizationSwitchPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useDocumentTitle(t('organizationSwitch:documentTitle'));

  const { selectOrganization, selectedOrganization } = useOrganizationContext();

  const {
    data: organizations,
    isPending: isPendingOrganizations,
    isError: isErrorOrganizations,
  } = useOrganizations();

  const [orgs, setOrgs] = useState<ComboboxOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization>();
  const [, setQuery] = useQueryParams({
    code: StringParam,
    state: StringParam,
  });

  useEffect(() => {
    if (
      !isPendingOrganizations &&
      !isErrorOrganizations &&
      organizations.length === 0
    ) {
      navigate('/unauthorized');
    }

    const itemsData = organizations?.map((org) => ({
      text: org.name,
      value: org.id,
    }));

    setOrgs(itemsData ?? []);
  }, [isErrorOrganizations, isPendingOrganizations, navigate, organizations]);

  useEffect(() => {
    if (selectedOrganization.id) {
      const prevPageUrl = window.localStorage.getItem(
        STORAGE_KEYS.AWN_PREVIOUS_PAGE_URL,
      );
      if (prevPageUrl) {
        window.localStorage.removeItem(STORAGE_KEYS.AWN_PREVIOUS_PAGE_URL);
        navigate(prevPageUrl);
      } else {
        navigate('/');
      }
    }
  }, [selectedOrganization.id, navigate]);

  const handleOrgSelect = useCallback(
    (changes: SelectOption | null) => {
      const selected = organizations?.find((o) => o.id === changes?.value);

      setSelectedOrg(selected);
    },
    [organizations],
  );

  const handleClick = useCallback(() => {
    if (selectedOrg) {
      selectOrganization(selectedOrg);
      setQuery({ code: undefined, state: undefined });
    }
  }, [selectedOrg, selectOrganization, setQuery]);

  useEffect(() => {
    if (organizations?.length === 1) {
      selectOrganization(organizations[0]);
    }
  }, [organizations, selectOrganization]);

  if (isErrorOrganizations || organizations?.length === 0) {
    return <SadWolfError />;
  }

  const isPossiblyGoingToAutoSelectOnlyOrganization = !organizations;
  if (
    isPossiblyGoingToAutoSelectOnlyOrganization ||
    organizations.length === 1
  ) {
    return <FullScreenLoader />;
  }

  return (
    <VStack gap="zero" height="full" width="full" overflow="hidden">
      <Primitive.div width="full" height="auto">
        <DashboardAppBar showOrganizationSwitcher={false} />
      </Primitive.div>

      <VStack
        width="full"
        alignItems="center"
        justifyContent="center"
        height="full"
      >
        <VStack
          padding="large"
          backgroundColor="widgetDefault"
          borderRadius="large"
          boxShadow="default"
          borderWidth="default"
          borderStyle="solid"
          borderColor="neutralSubtle"
          gap="large"
          css={{
            minWidth: size['30rem'],
          }}
        >
          <VStack>
            <Heading level="2">{t('organizationSwitch:title')}</Heading>
            <Text>{t('organizationSwitch:subTitle')}</Text>
          </VStack>
          <Combobox
            options={orgs}
            defaultText={t('organizationSwitch:emptyText')}
            filter="default"
            onSelection={handleOrgSelect}
            placeholder={t('organizationSwitch:selectOrganization')}
            isCreatable={false}
            isLoading={isPendingOrganizations}
          />
          <HStack xAlign="right" width="full">
            <Button
              variant="primary"
              onClick={handleClick}
              isDisabled={isEmpty(selectedOrg?.id)}
            >
              {t('organizationSwitch:getStarted')}
            </Button>
          </HStack>
        </VStack>
      </VStack>
    </VStack>
  );
};

export default OrganizationSwitchPage;
